import { Fragment } from 'react';
import { createPortal } from 'react-dom';

import { useIsSSR } from '@infinex/ui-utils/react-dom';

import {
  EMBLEM_DIMENSIONS,
  SWIDGE_CLIP_ID,
  SWIDGE_TO_DIAMETER_RATIO,
} from './constants';
import { shapePaths } from './shapePaths';

/**
 * Renders a single SVG containing the "activity" and "chain" adornment mask
 * definitions, within a portal at the end of the document body.
 */
export function EmblemSvgDefs() {
  if (useIsSSR()) {
    // Render inline during SSR so masks are available immediately
    return <SymbolsAndMasks />;
  }

  // Portal to document.body on client for performance
  return createPortal(<SymbolsAndMasks />, document.body);
}

const largestSize = Math.max(
  ...Object.values(EMBLEM_DIMENSIONS).map((dimensions) => dimensions.size)
);

const swidgeClipStroke = 0.0125;
const swidgeClipGutter = swidgeClipStroke * 2;
const swidgeClipRadius = SWIDGE_TO_DIAMETER_RATIO / 2;

function SymbolsAndMasks() {
  return (
    <svg
      aria-hidden
      focusable="false"
      tabIndex={-1}
      // Stop the SVG from taking up space in the document flow.
      height={0}
      width={0}
      // The `viewBox` is mostly irrelevant since the CSS `mask` property only
      // looks at the path data, it just needs to be large enough to contain the
      // largest size.
      viewBox={`0 0 ${largestSize} ${largestSize}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {Object.entries(EMBLEM_DIMENSIONS).map(([size, dimensions]) => {
          const { activityRect, chainRect, chainGroupRect } = dimensions;

          return (
            <Fragment key={size}>
              <clipPath id={dimensions.chainClipId}>
                <path
                  clipRule="evenodd"
                  d={shapePaths([
                    { height: dimensions.size, width: dimensions.size },
                    {
                      x: chainRect.left - chainRect.stroke,
                      y: chainRect.top - chainRect.stroke,
                      height: chainRect.height + chainRect.stroke * 2,
                      width: chainRect.width + chainRect.stroke * 2,
                      radius: chainRect.radius + chainRect.stroke,
                    },
                  ])}
                />
              </clipPath>

              <clipPath id={dimensions.activityClipId}>
                <path
                  clipRule="evenodd"
                  d={shapePaths([
                    { height: dimensions.size, width: dimensions.size },
                    {
                      x: activityRect.left - activityRect.stroke,
                      y: activityRect.top - activityRect.stroke,
                      height: activityRect.height + activityRect.stroke * 2,
                      width: activityRect.width + activityRect.stroke * 2,
                      radius: activityRect.radius + activityRect.stroke,
                    },
                  ])}
                />
              </clipPath>

              <clipPath id={dimensions.chainGroupClipId}>
                <path
                  clipRule="evenodd"
                  d={shapePaths([
                    { height: dimensions.size, width: dimensions.size },
                    {
                      x: chainGroupRect.left,
                      y: chainGroupRect.top,
                      height: chainGroupRect.height,
                      width: chainGroupRect.width,
                      radius: chainGroupRect.radius,
                    },
                  ])}
                />
              </clipPath>
            </Fragment>
          );
        })}

        {/*
          NOTE: The `clipPathUnits` attribute is set to `objectBoundingBox`,
          which means the path data is relative to the size of the element.
        */}
        <clipPath id={SWIDGE_CLIP_ID} clipPathUnits="objectBoundingBox">
          <path
            clipRule="evenodd"
            d={shapePaths([
              { width: 1, height: 1 },
              {
                cx: 1 - swidgeClipRadius - swidgeClipGutter,
                cy: swidgeClipRadius + swidgeClipGutter,
                r: swidgeClipRadius + swidgeClipGutter,
              },
            ])}
          />
        </clipPath>
      </defs>
    </svg>
  );
}
